/**
 * Convert a sequence of styled text items (one line's worth) into InlineRun[],
 * merging consecutive items that share the same bold/italic flags.
 *
 * Strategy:
 *   1. Filter out whitespace-only TextItems before processing — they're frequently
 *      PDF layout artefacts (sub-point-width "spaces") that conflate with the gap
 *      heuristic. Their actual word-boundary signal is preserved indirectly by the
 *      x-gap they create between surrounding meaningful items.
 *   2. Walk the meaningful items in x-order. Compute the gap between current item's
 *      x and previous item's xEnd:
 *        gap > GAP_SPACE_RATIO × line height → insert a space (real word boundary)
 *        gap ≤ threshold                     → flush join (intra-word fragment / ligature)
 *
 * GAP_SPACE_RATIO is set to ~0.15 — roughly half of a typical space character's
 * width (which is ~0.25–0.30 × font height). Tuned to split "In  the" while
 * keeping "th" + "e" → "the".
 */
const GAP_SPACE_RATIO = 0.15;
export function itemsToRuns(items) {
    // Drop pure-whitespace items — gaps speak for themselves.
    const meaningful = items.filter((it) => it.str.trim().length > 0);
    if (meaningful.length === 0)
        return [];
    const runs = [];
    let current = {
        text: meaningful[0].str,
        bold: meaningful[0].bold,
        italic: meaningful[0].italic,
    };
    let prevEnd = meaningful[0].xEnd;
    let prevHeight = meaningful[0].height;
    for (let i = 1; i < meaningful.length; i++) {
        const it = meaningful[i];
        const sameStyle = it.bold === current.bold && it.italic === current.italic;
        const gap = it.x - prevEnd;
        const sep = gap > GAP_SPACE_RATIO * Math.max(prevHeight, it.height) ? ' ' : '';
        if (sameStyle) {
            current.text += sep + it.str;
        }
        else {
            runs.push(current);
            current = { text: (sep === ' ' ? '' : '') + it.str, bold: it.bold, italic: it.italic };
            // Note: we do NOT prepend the separator inside a new run; markdown rendering
            // will join consecutive runs with a single space anyway via render's `runs.join(' ')`.
        }
        prevEnd = it.xEnd;
        prevHeight = it.height;
    }
    runs.push(current);
    return runs
        .map((r) => ({ ...r, text: r.text.replace(/\s+/g, ' ').trim() }))
        .filter((r) => r.text.length > 0);
}
/** Convenience: merge multiple lines' runs into one continuous InlineRun[] separated by spaces. */
export function mergeLineRuns(lines) {
    const merged = [];
    for (let i = 0; i < lines.length; i++) {
        const lineRuns = lines[i];
        if (merged.length > 0 && lineRuns.length > 0) {
            const last = merged[merged.length - 1];
            const first = lineRuns[0];
            if (last.bold === first.bold && last.italic === first.italic) {
                last.text += ' ' + first.text;
                for (let j = 1; j < lineRuns.length; j++)
                    merged.push(lineRuns[j]);
                continue;
            }
        }
        for (const r of lineRuns)
            merged.push(r);
    }
    return merged;
}
//# sourceMappingURL=emphasis.js.map